import { useMemo, useState } from 'react';
import includes from 'lodash/includes';
import {
  Divider,
  IconArchive,
  IconArchiveOutline,
  IconBankOutline,
  IconDollarSignCircleOutline,
  IconEditForm,
  IconEditFormOutline,
  IconExternal,
  IconHammer,
  IconHammerOutline,
  IconHouseTypeOutline,
  IconMessageOutline,
  IconTrendingOutline,
  Spacer,
  ZillowLogo,
} from '@zillow/constellation';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import useNavDisplay from '../../hooks/useNavDisplay';
import { registerTrackingEvent } from '../../api';
import HeaderNavigationClickstreamEvents from '../../models/HeaderNavigationClickstreamEvents';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import AdaptiveAnchor from '../AdaptiveAnchor';
import LogoZRMStacked from '../Logo/components/LogoZRMStacked';
import MenuIcon from '../MenuIcon';
import NavigationItem from '../VerticalNavigation/components/NavigationItem';
import NotificationCenter from '../NotificationCenter';
import PlusIconPropertyMenu from '../PlusIconPropertyMenu';
import { getPaymentsPath, shouldDisplayResources } from '../../utils';

import {
  StyledLinkListContainer,
  StyledLogoContainer,
  StyledMenuIconContainer,
  StyledMobileHeader,
  StyledMobileHeaderNavContainer,
} from './style';

import type { Props as HeaderNavigationProps } from '../../HeaderNavigation';
import type { User } from '../../interfaces/user';
import { CometUserRoleType } from '@zg-rentals/ts-rental-manager-api-client';

type Props = Partial<HeaderNavigationProps> & {
  altLogo?: boolean;
  isMobileMenuExpanded?: boolean;

  userModel: User;

  handleToggle: (arg0: boolean) => void;
};

const MobileHeaderNav = ({
  altLogo = true,
  brand = 'zillow',
  pathname = '',
  isMobileMenuExpanded: propIsMobileMenuExpanded = false,
  handleToggle,
  useRelativePaths,
  reactRouterLink,
  host,
  userModel = {} as User,
  ...rest
}: Props) => {
  const [isMobileMenuExpanded, setIsMobileMenuExpanded] = useState(propIsMobileMenuExpanded);

  const { displays } = useNavDisplay({ userModel });
  const {
    displayAnalytics,
    displayBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainBilling,
    displayMainHelp,
    displayMainLeads,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
    displayMainNotificationCenter,
    displayMainPayments,
    displayMainResources,
    displayOnboardingTasks,
    displayUserManagement,
  } = displays;
  const { features = {}, loggedIn, paymentsAcceptedCount, hasSeenNewPaymentsPage } = userModel;
  const { canViewClaimFeedListing: canAccessAddFeedListingsModal = false } = features as User['features'];

  const { csrf_token = '', escapeAllLinks, proxyPath = '', webSocketInstance, zgApi = '' } = rest;

  // Pass link component from either react-router or next-js
  // const Link = v4Link;
  const Link = reactRouterLink;

  const useAbsolutePaths = !useRelativePaths || !reactRouterLink;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, host).getAllPaths();

  const {
    adManagementPage,
    analyticsOverview,
    analyticsPage,
    billingDue,
    billingPage,
    helpPage = '',
    homePage,
    inboxPage,
    leadsPage,
    maintenanceTrackerPage,
    mfhResourcesPage,
    paymentsAggregatePage,
    paymentsGetStartedPage,
    rentPaymentPage,
    payments,
    rentPayment,
    postLogout = '',
    propertiesPage,
    resourcesPage,
    setupTasks,
    userManagementPage,
    userSettingsPage = '',
    adManagementTabsPage,
  } = paths;

  const isPageActive = useMemo(() => {
    return ({ pageUrl }: { pageUrl: string }) => {
      return includes(pathname, pageUrl);
    };
  }, [pathname]);

  const closeMobileMenu = () => {
    setIsMobileMenuExpanded(false);
  };

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];
      if (pageClickingTo === HeaderTitles.payments && hasSeenNewPaymentsPage === false) {
        registerTrackingEvent(proxyPath, csrf_token, zgApi, 'openNewPaymentsPage');
      }

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }

    closeMobileMenu();
  };

  const handleClickPropertyMenuItem = (event: React.SyntheticEvent) => {
    // prevent parent navigation
    event.stopPropagation();

    handleLinkClick(HeaderTitles.myProperties);
  };

  const isPropertiesPageActive = includes(pathname, propertiesPage);
  const isPaymentPageActive = includes(pathname, payments) || includes(pathname, rentPayment);
  const isNonAdvertiser = userModel?.roles?.includes(CometUserRoleType.NON_ADVERTISER_CUSTOMER);

  return (
    <StyledMobileHeaderNavContainer
      data-testid="StyledMobileHeaderNavContainer"
      isMobileMenuExpanded={isMobileMenuExpanded}
    >
      <StyledMobileHeader>
        <StyledMenuIconContainer>
          <MenuIcon
            isExpanded={isMobileMenuExpanded}
            handleToggle={(isExpanded) => {
              setIsMobileMenuExpanded(isExpanded);
              handleToggle(isExpanded);
            }}
          />
        </StyledMenuIconContainer>

        <StyledLogoContainer altLogo={altLogo}>
          <AdaptiveAnchor
            escape={escapeAllLinks}
            link={Link}
            onClick={closeMobileMenu}
            path={propertiesPage}
            useAbsolutePaths={useAbsolutePaths}
          >
            {altLogo ? <LogoZRMStacked /> : <ZillowLogo />}
          </AdaptiveAnchor>
        </StyledLogoContainer>

        <>
          {loggedIn && displayMainNotificationCenter ? (
            <NotificationCenter
              brand={brand}
              proxyPath={proxyPath}
              csrf_token={csrf_token}
              webSocketInstance={webSocketInstance}
              zgApi={zgApi}
            />
          ) : (
            // need an element here to layout the other elements via flex correctly
            <div />
          )}
        </>
      </StyledMobileHeader>

      {isMobileMenuExpanded && (
        <StyledLinkListContainer>
          <Spacer marginBottom="sm" />

          {/* properties */}
          {loggedIn && displayMainMyProperties && (
            <NavigationItem
              isActive={isPropertiesPageActive}
              isMobileWeb={true}
              title={HeaderTitles.myProperties}
              escape={escapeAllLinks}
              path={propertiesPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconHouseTypeOutline size="sm" />}
              InactiveIcon={<IconHouseTypeOutline size="sm" />}
              TrailingIcon={
                <PlusIconPropertyMenu
                  Link={Link}
                  canAccessAddFeedListingsModal={canAccessAddFeedListingsModal}
                  escapeAllLinks={escapeAllLinks}
                  handleNoFeedIconPlusClick={handleLinkClick}
                  onClickPropertyMenuItem={handleClickPropertyMenuItem}
                  propertiesPage={propertiesPage}
                  useAbsolutePaths={useAbsolutePaths}
                  userModel={userModel}
                />
              }
              onClick={() => {
                handleLinkClick(HeaderTitles.myProperties);
              }}
            />
          )}

          {/* messages */}
          {displayMainMessages && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: inboxPage })}
              isMobileWeb={true}
              title={HeaderTitles.messages}
              escape={true}
              path={inboxPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconMessageOutline size="sm" />}
              InactiveIcon={<IconMessageOutline size="sm" />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.messages);
              }}
            />
          )}

          {/* maintenance */}
          {displayMainMaintenanceTracker && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: maintenanceTrackerPage })}
              isMobileWeb={true}
              title={HeaderTitles.maintenanceTracker}
              escape={true}
              path={maintenanceTrackerPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconHammer size="sm" />}
              InactiveIcon={<IconHammerOutline size="sm" />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.maintenanceTracker);
              }}
            />
          )}

          {/* contacts */}
          {displayMainLeads && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: leadsPage })}
              title={HeaderTitles.leads}
              escape={true}
              path={leadsPage}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconMessageOutline size="sm" />}
              InactiveIcon={<IconMessageOutline size="sm" />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.leads);
              }}
            />
          )}

          {/* payments */}
          {loggedIn && displayMainPayments && (
            <NavigationItem
              isActive={isPropertiesPageActive ? false : isPaymentPageActive}
              title={HeaderTitles.payments}
              escape={true}
              path={getPaymentsPath({
                paymentsAcceptedCount,
                paymentsAggregatePage,
                paymentsGetStartedPage,
                rentPaymentPage,
              })}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconDollarSignCircleOutline size="sm" />}
              InactiveIcon={<IconDollarSignCircleOutline size="sm" />}
              onClick={() => {
                handleLinkClick(HeaderTitles.payments);
              }}
            />
          )}

          {/* analytics */}
          {displayAnalytics && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: analyticsPage })}
              title={HeaderTitles.analytics}
              escape={true}
              path={`${analyticsPage}/${analyticsOverview}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconTrendingOutline size="sm" />}
              InactiveIcon={<IconTrendingOutline size="sm" />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.analytics);
              }}
            />
          )}

          {/* billing */}
          {displayMainBilling && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: billingPage })}
              title={HeaderTitles.billing}
              escape={true}
              path={`${billingPage}/${billingDue}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconBankOutline size="sm" />}
              InactiveIcon={<IconBankOutline size="sm" />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.billing);
              }}
            />
          )}

          {/* ad management */}
          {loggedIn && userModel.isEnterpriseCustomer && (
            <NavigationItem
              isActive={includes(pathname, adManagementTabsPage)}
              title={HeaderTitles.adManagement}
              escape={true}
              path={`${adManagementPage}/${adManagementTabsPage}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              InactiveIcon={<IconArchiveOutline size="sm" />}
              ActiveIcon={<IconArchive size="sm" />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.adManagement);
              }}
            />
          )}

          {/* onboarding tasks */}
          {displayOnboardingTasks && (
            <NavigationItem
              isActive={isPageActive({ pageUrl: setupTasks })}
              title={HeaderTitles.onboardingTasks}
              escape={true}
              path={`${adManagementPage}/${setupTasks}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconEditForm size="sm" />}
              InactiveIcon={<IconEditFormOutline size="sm" />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.onboardingTasks);
              }}
            />
          )}

          <Divider marginY="sm" />

          {/* account settings */}
          {displayMainAccountSettings && (
            <NavigationItem
              title={HeaderTitles.accountSettings}
              path={userSettingsPage}
              target="_blank"
              TrailingIcon={<IconExternal />}
              onClick={() => handleLinkClick(HeaderTitles.accountSettings)}
            />
          )}

          {/* billing - premium listing */}
          {displayBilling && !displayMainBilling && (
            <NavigationItem
              title={HeaderTitles.billing}
              path={`${billingPage}/${billingDue}`}
              marginBottom={0}
              onClick={() => handleLinkClick(HeaderTitles.billing)}
            />
          )}

          {/* user management  */}
          {displayUserManagement && (
            <NavigationItem
              title={HeaderTitles.userManagement}
              path={userManagementPage}
              onClick={() => handleLinkClick(HeaderTitles.userManagement)}
            />
          )}

          <Divider marginY="sm" />

          {/* resources */}
          {shouldDisplayResources(displayMFHResources, displayMainResources) && (
            <>
              {displayMFHResources ? (
                <NavigationItem
                  title={HeaderTitles.mfhResources}
                  path={mfhResourcesPage}
                  target="_blank"
                  TrailingIcon={<IconExternal />}
                  onClick={() => handleLinkClick(HeaderTitles.mfhResources)}
                />
              ) : (
                <NavigationItem
                  title={HeaderTitles.resources}
                  path={resourcesPage}
                  target="_blank"
                  TrailingIcon={<IconExternal />}
                  onClick={() => handleLinkClick(HeaderTitles.resources)}
                />
              )}
            </>
          )}

          {/* help */}
          {displayMainHelp && (
            <NavigationItem
              title={HeaderTitles.help}
              path={helpPage}
              target="_blank"
              TrailingIcon={<IconExternal />}
              onClick={() => handleLinkClick(HeaderTitles.help)}
            />
          )}

          <Divider marginY="sm" />

          {/* go to Zillow  */}
          <NavigationItem
            title={HeaderTitles.backToBrand}
            path={homePage}
            target="_blank"
            TrailingIcon={<IconExternal />}
            onClick={() => handleLinkClick(HeaderTitles.backToBrand)}
          />

          {/* sign out  */}
          <NavigationItem
            title={HeaderTitles.signOut}
            path={postLogout}
            escape={true}
            onClick={() => handleLinkClick(HeaderTitles.signOut)}
          />
        </StyledLinkListContainer>
      )}
    </StyledMobileHeaderNavContainer>
  );
};

export default MobileHeaderNav;
